import { createSteeringURL } from '@/api'
import { useI18n } from '@/i18n'
import { steeringAuthInit } from '@/stores/auth'
import { Media, MediaCreateData, MediaSource, MediaUpdateData } from '@/types'
import { calculateDurationSeconds } from '@/util'
import { aggregateWithIdsParameter, useStoreHelpers } from '@/util/api'
import {
  APICreate,
  APIListPaginated,
  APIRemove,
  APIRetrieve,
  APIUpdate,
  createExtendableAPI,
} from '@rokoli/bnb/drf'
import { defineStore } from 'pinia'
import { computed, ComputedRef, MaybeRefOrGetter, toValue } from 'vue'

export function calculateMediaDurationInSeconds(
  mediaSources: MediaSource[],
  skipUnknown?: true,
): number
export function calculateMediaDurationInSeconds(
  mediaSources: MediaSource[],
  skipUnknown?: false,
): number | null

/**
 * Calculates the duration of a media container.
 * May return null if the media object contains entries with unset duration.
 * @param mediaSources
 * @param skipUnknown Whether unknown length entries should be skipped
 */
export function calculateMediaDurationInSeconds(mediaSources: MediaSource[], skipUnknown = false) {
  let duration = 0
  for (const mediaSource of mediaSources) {
    // mediaSource.duration may be null/NaN if the media source references
    // a stream or other resources without an inherent duration
    if (typeof mediaSource.duration !== 'number' || isNaN(mediaSource.duration)) {
      if (skipUnknown) continue
      else return null
    }
    duration += mediaSource.duration
  }

  return duration
}

export function countUnknownDurations(mediaSources: MediaSource[]) {
  let counter = 0
  for (const mediaSource of mediaSources) {
    if (typeof mediaSource.duration !== 'number' || isNaN(mediaSource.duration)) counter += 1
  }
  return counter
}

type MediaInheritanceSource = 'self' | 'show' | 'schedule' | 'episode'

export function useMediaCascade(
  resolvedMedia: MaybeRefOrGetter<{ id: Media['id']; from: MediaInheritanceSource } | null>,
  treatAsSelf: MediaInheritanceSource[] = ['self'],
) {
  const mediaStore = useMediaStore()
  const { obj: media, isLoading: isEvaluatingMedia } = mediaStore.use(
    () => toValue(resolvedMedia)?.id,
  )
  const mediaInheritedFrom = computed(() => toValue(resolvedMedia)?.from ?? null)
  const mediaInheritanceLabel = computed(() => {
    if (isEvaluatingMedia.value) return undefined
    if (!mediaInheritedFrom.value) return undefined
    if (treatAsSelf.includes(mediaInheritedFrom.value)) return undefined
    const { t } = useI18n()
    return t(`media.inheritance.${mediaInheritedFrom.value}`)
  })

  return { media, isEvaluatingMedia, mediaInheritedFrom, mediaInheritanceLabel }
}

type MediaState =
  | 'ok'
  | 'ok.autoExpanded'
  | 'invalid.missing'
  | 'invalid.multipleUnknownDurations'
  | 'invalid.tooShort'
  | 'invalid.tooLong'

export type ResolvedMediaState = {
  state: MediaState
  durationSeconds: number
  offsetSeconds: number
}

export function useMediaState(
  media: MaybeRefOrGetter<Media | null>,
  targetDurationSeconds?:
    | MaybeRefOrGetter<number | undefined | null | { start: string | Date; end: string | Date }>
    | undefined,
): ComputedRef<ResolvedMediaState> {
  const targetDuration = computed(() => {
    const v = toValue(targetDurationSeconds)
    if (!v) return null
    if (typeof v === 'number') return v
    return calculateDurationSeconds(v.start, v.end)
  })

  return computed(() => {
    const sources = toValue(media)?.entries

    if (sources === undefined) {
      return { state: 'invalid.missing', durationSeconds: 0, offsetSeconds: 0 }
    }

    if (sources.length === 0) {
      // With no sources the media cascade will just select,
      // the next media container for playout, so: this is fine.
      return { state: 'ok', durationSeconds: 0, offsetSeconds: 0 }
    }

    const _targetDuration = toValue(targetDuration)
    const roundedTargetDuration =
      typeof _targetDuration === 'number' ? Math.round(_targetDuration) : null

    const unknownDurationCount = countUnknownDurations(sources)
    if (unknownDurationCount > 1) {
      // This should never happen because steering validates that we don’t have more than one
      // source of unknown duration, but better safe than sorry.
      return {
        state: 'invalid.multipleUnknownDurations',
        durationSeconds: 0,
        offsetSeconds: 0,
      }
    }

    const staticMediaDuration = Math.round(calculateMediaDurationInSeconds(sources, true))
    if (roundedTargetDuration !== null && staticMediaDuration > roundedTargetDuration) {
      return {
        state: 'invalid.tooLong',
        durationSeconds: staticMediaDuration,
        offsetSeconds: staticMediaDuration - roundedTargetDuration,
      }
    }

    // If the media contains just one record of unknown length
    // the playout will automatically expand that entry to the remaining
    // time that is needed to fill the timeslot.
    // We can therefore consider a single entry of unknown duration to fit the required time.
    if (unknownDurationCount === 1 && roundedTargetDuration !== null) {
      return {
        state: 'ok.autoExpanded',
        durationSeconds: roundedTargetDuration,
        offsetSeconds: Math.round(roundedTargetDuration - staticMediaDuration),
      }
    }

    // There aren’t any sources that are auto-expanded, so if the static media duration is
    // too short, and we have a target duration to reach, the sources won’t fill them entirely.
    if (roundedTargetDuration !== null && staticMediaDuration < roundedTargetDuration) {
      return {
        state: 'invalid.tooShort',
        durationSeconds: staticMediaDuration,
        offsetSeconds: roundedTargetDuration - staticMediaDuration,
      }
    }

    return {
      state: 'ok',
      durationSeconds: staticMediaDuration,
      offsetSeconds: 0,
    }
  })
}

export const useMediaStore = defineStore('media', () => {
  const endpoint = createSteeringURL.prefix('media')
  const { api, base } = createExtendableAPI<Media>(endpoint, steeringAuthInit)
  const listOperations = APIListPaginated(api)
  const retrieveOperations = APIRetrieve(api, {
    aggregate: aggregateWithIdsParameter(listOperations.listIsolated),
  })
  return {
    ...base,
    ...listOperations,
    ...retrieveOperations,
    ...APICreate<Media, MediaCreateData>(api),
    ...APIUpdate<Media, MediaUpdateData, MediaUpdateData>(api),
    ...APIRemove(api),
    ...useStoreHelpers({ ...base, ...listOperations, ...retrieveOperations }),
  }
})
